import { useEffect, useState } from "react";
import { History, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ENTITIES = {
  cars: "Mobil", purchases: "Pembelian", costs: "Biaya Mobil", sales: "Penjualan",
  users: "Pengguna", branches: "Cabang", "cash-entries": "Entri Kas", "op-costs": "Biaya Operasional",
};

const ACTIONS = {
  tambah: { label: "Tambah", cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
  ubah: { label: "Ubah", cls: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  hapus: { label: "Hapus", cls: "text-rose-500 bg-rose-500/10 border-rose-500/30" },
};

const FIELD_LABELS = {
  merk: "Merk", model: "Model", year: "Tahun", color: "Warna", plate: "Plat", km: "KM", status: "Status",
  branch_id: "Cabang", price: "Harga", amount: "Jumlah", dp_amount: "DP", received_amount: "Dana Diterima",
  buyer_name: "Pembeli", leasing_name: "Leasing", description: "Keterangan", notes: "Catatan",
  asking_cash_price: "Harga Cash", asking_credit_price: "Harga Kredit", date: "Tanggal", category: "Kategori",
  show_in_pricelist: "Tampil di Pricelist", supplier_name: "Penjual/Supplier",
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "(kosong)";
  if (typeof v === "boolean") return v ? "Ya" : "Tidak";
  if (typeof v === "number") return new Intl.NumberFormat("id-ID").format(v);
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const fmtAt = (at) =>
  new Date(at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function actionMeta(a) {
  return ACTIONS[a] || { label: a, cls: "text-blue-400 bg-blue-500/10 border-blue-500/30" };
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const meta = actionMeta(log.action);
  const detail = log.changes?.length ? log.changes : null;
  const extra = log.before || (Object.keys(log.payload || {}).length ? log.payload : null);
  return (
    <>
      <tr className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap text-xs">{fmtAt(log.at)}</td>
        <td className="px-4 py-3">
          <p className="text-zinc-100 text-sm font-medium">{log.user_name}</p>
          <p className="text-[11px] text-zinc-500">{log.user_email}</p>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${meta.cls}`}>
            {meta.label}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-zinc-400">{log.entity_label || log.entity}</td>
        <td className="px-4 py-3 text-sm text-zinc-200">{log.target || "-"}</td>
        <td className="px-4 py-3 text-right">
          {(detail || extra) && (
            <button
              data-testid={`audit-detail-${log.id}`}
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200"
            >
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Detail
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-[#1A1A22]/60 bg-[#08080A]">
          <td colSpan={6} className="px-6 py-4">
            {detail ? (
              <ul className="space-y-1.5" data-testid={`audit-changes-${log.id}`}>
                {detail.map((c, i) => (
                  <li key={i} className="text-xs text-zinc-300">
                    <span className="text-zinc-500">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
                    <span className="text-rose-400 line-through">{fmtVal(c.from)}</span>{" "}
                    <span className="text-zinc-600">&rarr;</span>{" "}
                    <span className="text-emerald-400">{fmtVal(c.to)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="grid gap-1.5 sm:grid-cols-2" data-testid={`audit-payload-${log.id}`}>
                {Object.entries(extra || {}).map(([k, v]) => (
                  <p key={k} className="text-xs text-zinc-300">
                    <span className="text-zinc-500">{FIELD_LABELS[k] || k}:</span> {fmtVal(v)}
                  </p>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditLog() {
  const [data, setData] = useState(null);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [limit, setLimit] = useState(200);

  const load = () => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (entity !== "all") p.set("entity", entity);
    if (action !== "all") p.set("action", action);
    if (q) p.set("q", q);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    api.get(`/audit-logs?${p}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [entity, action, dateFrom, dateTo, limit]);

  return (
    <div data-testid="audit-page">
      <PageHeader title="Riwayat Perubahan" subtitle="Siapa mengubah, menambah, atau menghapus data — dan kapan">
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="audit-entity-filter" className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Data</SelectItem>
            {Object.entries(ENTITIES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="audit-action-filter" className="w-36 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Aksi</SelectItem>
            <SelectItem value="tambah">Tambah</SelectItem>
            <SelectItem value="ubah">Ubah</SelectItem>
            <SelectItem value="hapus">Hapus</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="audit-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]" />
        <Input data-testid="audit-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]" />
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2">
          <Input data-testid="audit-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari mobil / pengguna" className="w-52 h-10 bg-[#0F0F12] border-[#1A1A22]" />
          <Button data-testid="audit-search-button" type="submit" className="h-10 bg-blue-600 hover:bg-blue-500 text-white font-semibold">Cari</Button>
        </form>
      </PageHeader>

      {!data ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : data.logs.length === 0 ? (
        <EmptyState icon={History} title="Belum ada riwayat" desc="Perubahan data akan tercatat otomatis di sini" />
      ) : (
        <>
          <p className="text-xs text-zinc-500 mb-3" data-testid="audit-total">Menampilkan {data.logs.length} dari {data.total} catatan</p>
          <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
            <table className="w-full text-sm min-w-[860px]" data-testid="audit-table">
              <thead>
                <tr className="border-b border-[#1A1A22] text-left">
                  {["Waktu", "Pengguna", "Aksi", "Jenis Data", "Objek", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.logs.map((l) => <LogRow key={l.id} log={l} />)}
              </tbody>
            </table>
          </div>
          {data.total > data.logs.length && (
            <div className="mt-4 flex justify-center">
              <Button data-testid="audit-load-more" variant="outline" onClick={() => setLimit((l) => l + 200)} className="border-[#26262E] text-zinc-300 hover:bg-[#121218] h-10">
                Muat lebih banyak
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
